// eslint-disable-next-line import/no-cycle
import type { Fields } from "@vuex-orm/core";
import { chunk, sum } from "lodash";
import { BrowserAction } from "./ActionHierarchy";
import type { ActionWithDuration, Timings } from "./ActionTypes";

export class Input extends BrowserAction implements ActionWithDuration {
  // This is the name used as module name of the Vuex Store.
  public static entity = "inputs";

  public static baseEntity = "actions";

  public static fields(): Fields {
    return {
      ...super.fields(),
      text: this.string(""),
      textReplay: this.string("paste"),
      password: this.attr(""), // TODO: probably create a PasswordInput extends Input class, that
      // stores its data on the server (encrypted). We probably never need the unencrypted
      // password again in the client. So basically it could be some kind of Secrets API on the
      // backend.
    };
  }

  public declare text: string;

  // public declare textReplay: "clean" | "original" | "paste";
  public declare textReplay: "original" | "paste";

  public declare password: string;

  public get skippableBetween(): [number, number][] {
    if (!this.startsAt) return [];

    if (this.textReplay === "paste") {
      const lastEvent = this.events[this.events.length - 1];
      if (!lastEvent) return [];

      return [[this.startsAt + 1, lastEvent.timestamp - 1]];
    }

    return [];
  }

  public get playableRanges(): (number[] | number)[] {
    if (!this.startsAt || !this.endsAt) return [];
    return chunk<number>([this.startsAt, ...this.skippableBetween.flat(), this.endsAt], 2);
  }

  public get duration(): number {
    return sum(
      this.playableRanges.map((range) => {
        if (!Array.isArray(range) || range.length < 2) return 0;

        const [from, to] = range as [number, number];
        return (to - from) / 1000;
      }),
    );
  }

  public get defaultTimings(): Pick<Timings, "before" | "main" | "after"> {
    if (this.speed === "fast")
      return {
        before: 0.1,
        main: 0.25,
        after: 0.1,
      };

    if (this.speed === "medium")
      return {
        before: 0.3,
        main: 0.25,
        after: 0.4,
      };

    return {
      before: 0.6,
      main: 0.25,
      after: 1,
    };
  }

  public get elementPreview(): Element | null {
    if (super.elementPreview) {
      const elementPreview = super.elementPreview;
      elementPreview.setAttribute("value", this.text);
      return elementPreview;
    }
    return null;
  }

  public get isPassword(): boolean {
    return !!(
      this.elementPreview &&
      this.elementPreview.tagName === "INPUT" &&
      this.elementPreview.getAttribute("type") === "password"
    );
  }

  public get verb(): string {
    return "fill out";
  }

  public static get humanName(): string {
    return "Type";
  }

  public get description(): string {
    return `Type “${this.text}”`;
  }
}
